import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectParticipantAttendedModules } from '../../../../store/courseSelectors';
import { loadBloodTestEvents, loadBloodTestSources } from '../../../../store/directusService';
import {
    clearBloodTests,
    deleteBloodTest,
    loadBloodTests
} from '../../../../store/participantService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    BLOOD_TEST_EVENTS_ERROR,
    BLOOD_TEST_SOURCES_ERROR,
    BLOOD_TESTS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import BloodTestRow from './BloodTestRow';
import EditBloodTest from './EditBloodTest';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date' },
    { id: 'testType', numeric: false, label: 'Test Type' },
    { id: 'testValue', numeric: true, label: 'Test Value' },
    { id: 'testValueType', numeric: false, label: 'Test Value Type' },
    { id: 'testResult', numeric: false, label: 'Test Result' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const BloodTest = ({
    participant,
    isEditDisabled = true,
    isDeleteDisabled = true,
    isCreateDisabled = true,
    isAuditable,
    onSearchAuditItems,
    linkedModuleId
}) => {
    const dispatch = useDispatch();
    const bloodTestRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [modules, setModules] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const bloodTests = useSelector((state) => state.entities.participantService.bloodTests);
    const bloodTestsMetaData = useSelector(
        (state) => state.entities.participantService.bloodTestsMetaData
    );
    const attendedModules = useSelector(selectParticipantAttendedModules);
    const bloodTestEvents = useSelector((state) => state.entities.directusService.bloodTestEvents);
    const bloodTestSources = useSelector(
        (state) => state.entities.directusService.bloodTestSources
    );

    // USEEFFECTS
    useEffect(() => {
        bloodTestEvents?.length < 1 && dispatch(loadBloodTestEvents());
        bloodTestSources?.length < 1 && dispatch(loadBloodTestSources());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        participant?.id && dispatch(loadBloodTests(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (bloodTestEvents?.length < 1 || bloodTestSources?.length < 1) return;
        if (bloodTestsMetaData?.loaded) {
            let modules = [];
            if (!bloodTests?.length) {
                rows.length && setRows([]);
                if (attendedModules?.length) modules = [...attendedModules];
                setLoadingContentMessage(BLOOD_TESTS_ERROR);
            } else {
                const rows = bloodTests.map((el) => ({
                    ...el,
                    testValue: el.testType === 'HbA1c' ? el.testValueHb : el.testValueFpg,
                    event: getNameFromId(bloodTestEvents, el.eventId),
                    source: getNameFromId(bloodTestSources, el.sourceId),
                    linkedModule: el.linkedModuleId
                        ? attendedModules?.find((entry) => entry.id === el.linkedModuleId)?.title
                        : ''
                }));
                setRows(rows);

                if (attendedModules?.length) {
                    modules = attendedModules.filter(
                        (el) => !bloodTests.find((entry) => entry.linkedModuleId === el.id)
                    );
                }
            }
            setModules(modules.map((el) => ({ ...el, name: el.title })) || []);
        }
    }, [bloodTests, attendedModules, bloodTestEvents, bloodTestSources, bloodTestsMetaData]);

    // FNs
    const loadingError = () => {
        if (bloodTestEvents?.length < 1) return BLOOD_TEST_EVENTS_ERROR;
        if (bloodTestSources?.length < 1) return BLOOD_TEST_SOURCES_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <BloodTestRow
                key={el.id}
                row={el}
                isDisabled={isEditDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                toggleDropdown={setOpenId}
                openId={openId}
                isAuditable={isAuditable}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));

    const cleanUp = () => {
        dispatch(clearBloodTests());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setFormType('edit');
        setSelectedRow(row);
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteBloodTest(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(bloodTestRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${local.wellBeingSection} ${app.fadein}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    // RENDER
    return (
        <div
            className={`${local.wellBeingSection} ${app.fadein}`}
            data-testid="blood_tests"
            ref={bloodTestRef}>
            <h2 className={app.sectionHeading}>Blood Test</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        defaultRowsPerPage={rowMetaData.rowsPerPage}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addBloodTestButton"
                        type="button"
                        content="Add New Blood Test"
                        disabled={isCreateDisabled}
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <EditBloodTest
                        participant={participant}
                        onClose={onClose}
                        isDisabled={
                            (formType === 'edit' && isEditDisabled) ||
                            (formType === 'create' && isCreateDisabled)
                        }
                        formType={formType}
                        row={selectedRow}
                        attendedModules={modules}
                        linkedModuleId={linkedModuleId || ''}
                    />
                )}
            </div>
        </div>
    );
};

BloodTest.propTypes = {
    participant: PropTypes.object.isRequired,
    isEditDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isCreateDisabled: PropTypes.bool,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func,
    linkedModuleId: PropTypes.string
};

export default BloodTest;
